import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { sankey as d3sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { SANKEY_GRAY, SANKEY_PALETTE, fmt } from './palette';

export interface SankeyNodeIn {
    name: string;
    kind: 'total' | 'segment' | 'category' | 'event';
    slot: number;
    users?: number;
    new_events?: number;
}
export interface SankeyGraphIn {
    nodes: SankeyNodeIn[];
    links: Array<{ source: number; target: number; value: number }>;
}

interface Props {
    graph: SankeyGraphIn | undefined;
    theme: 'light' | 'dark';
    rootRef: React.RefObject<HTMLDivElement>;
}

/** d3-sankey activity flow: total → New/Returning → category → event.
 *  Flow unit is EVENT COUNT (users are tooltip metadata). Auto-height,
 *  horizontally scrollable, never cropped. */
export const Sankey: React.FC<Props> = ({ graph, theme, rootRef }) => {
    const boxRef = useRef<HTMLDivElement | null>(null);
    const tipRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = boxRef.current;
        if (!el) return undefined;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const render = () => {
            el.innerHTML = '';
            if (!graph || !graph.nodes.length) {
                el.innerHTML = '<div class="pr-empty">No events in this window.</div>';
                return;
            }
            const pal = SANKEY_PALETTE[theme];
            const color = (n: SankeyNodeIn) =>
                n.slot == null || n.slot < 0 || n.slot >= pal.length ? SANKEY_GRAY : pal[n.slot];
            const ink = rootRef.current
                ? getComputedStyle(rootRef.current).getPropertyValue('--fg').trim() : '#ddd';
            const leaves = graph.nodes.filter((n) => n.kind === 'event').length;
            const width = Math.max(el.parentElement?.clientWidth ?? 900, 760);
            const height = Math.max(440, leaves * 27 + 60);

            type N = SankeyNodeIn & { x0: number; x1: number; y0: number; y1: number; value: number };
            const layout = d3sankey<SankeyNodeIn, { value: number }>()
                .nodeWidth(12).nodePadding(11)
                .nodeSort(null as never).linkSort(null as never)  // keep the report's order
                .extent([[0, 12], [width, height - 12]]);
            const { nodes, links } = layout({
                nodes: graph.nodes.map((d) => ({ ...d })),
                links: graph.links.map((d) => ({ ...d })),
            });
            const grandTotal = (nodes[0] as N)?.value ?? 0;

            const svg = d3.create('svg')
                .attr('width', width).attr('height', height)
                .attr('font-family', 'Inter, system-ui, sans-serif').attr('font-size', 11.5)
                .attr('role', 'img');

            const tip = tipRef.current;
            const showTip = (event: MouseEvent, html: string) => {
                if (!tip) return;
                tip.innerHTML = html;
                tip.style.display = 'block';
                const pad = 14;
                tip.style.left = `${Math.min(event.clientX + pad, window.innerWidth - tip.offsetWidth - 8)}px`;
                tip.style.top = `${event.clientY + pad}px`;
            };
            const hideTip = () => { if (tip) tip.style.display = 'none'; };

            // total→segment wears the segment (target) color; everything else its
            // source color, so each category visibly splits new vs returning.
            const linkColor = (l: { source: N; target: N }) =>
                l.source.kind === 'total' ? color(l.target) : color(l.source);
            const baseOpacity = theme === 'light' ? 0.32 : 0.42;
            svg.append('g').attr('fill', 'none')
                .selectAll('path').data(links as never[]).join('path')
                .attr('d', sankeyLinkHorizontal() as never)
                .attr('stroke', (d) => linkColor(d as never))
                .attr('stroke-width', (d) => Math.max(1, (d as { width: number }).width))
                .attr('stroke-opacity', baseOpacity)
                .on('mousemove', function onMove(event: MouseEvent, d) {
                    d3.select(this).attr('stroke-opacity', 0.62);
                    const l = d as unknown as { source: N; target: N; value: number };
                    showTip(event, `<strong>${l.source.name} → ${l.target.name}</strong><br>${fmt(l.value)} events`);
                })
                .on('mouseleave', function onLeave() {
                    d3.select(this).attr('stroke-opacity', baseOpacity);
                    hideTip();
                });

            svg.append('g').selectAll('rect').data(nodes as never[]).join('rect')
                .attr('x', (d) => (d as N).x0).attr('y', (d) => (d as N).y0)
                .attr('width', (d) => (d as N).x1 - (d as N).x0)
                .attr('height', (d) => Math.max(1, (d as N).y1 - (d as N).y0))
                .attr('rx', 2).attr('fill', (d) => color(d as N))
                .on('mousemove', (event: MouseEvent, d) => {
                    const n = d as N;
                    let html = `<strong>${n.name}</strong><br>${fmt(n.value)} events`;
                    if (grandTotal && n.kind !== 'total') {
                        html += ` · ${((100 * n.value) / grandTotal).toFixed(1)}% of all`;
                    }
                    if (n.kind === 'event') {
                        html += `<br>${fmt(n.users ?? 0)} unique users · ${fmt(n.new_events ?? 0)} events from new users`;
                    }
                    if (n.kind === 'segment') {
                        html += n.slot === 0
                            ? '<br>Users first seen inside the window'
                            : '<br>Users first seen before the window';
                    }
                    showTip(event, html);
                })
                .on('mouseleave', hideTip);

            svg.append('g').selectAll('text').data(nodes as never[]).join('text')
                .attr('x', (d) => ((d as N).x0 < width / 2 ? (d as N).x1 + 6 : (d as N).x0 - 6))
                .attr('y', (d) => ((d as N).y0 + (d as N).y1) / 2)
                .attr('dy', '0.35em')
                .attr('text-anchor', (d) => ((d as N).x0 < width / 2 ? 'start' : 'end'))
                .attr('fill', ink)
                .text((d) => `${(d as N).name}: ${fmt((d as N).value)}`);

            const node = svg.node();
            if (node) el.appendChild(node);
        };
        render();
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(render, 200);
        };
        window.addEventListener('resize', onResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [graph, theme, rootRef]);

    const pal = SANKEY_PALETTE[theme];
    const legend = (graph?.nodes ?? []).filter((n) => n.kind === 'segment' || n.kind === 'category');
    return (
        <>
            <div className="pr-legend">
                {legend.map((n) => (
                    <span key={n.name}>
                        <span className="sw" style={{ background: n.slot >= 0 && n.slot < pal.length ? pal[n.slot] : SANKEY_GRAY }} />
                        {n.name}
                    </span>
                ))}
            </div>
            <div className="pr-sankey-scroll"><div ref={boxRef} /></div>
            <div ref={tipRef} className="pr-tooltip" />
        </>
    );
};
